import React from "react";
import { useTranslation } from "react-i18next";

interface PaginationProps {
    offset: number;
    total: number;
    pageSize: number;
    onPrev: () => void;
    onNext: () => void;
    className?: string;
}

/**
 * Previous / next through a list read a page at a time, and where in it the
 * reader is. Nothing at all when everything fits on one page: controls that
 * can only be disabled are noise.
 */
export const Pagination: React.FC<PaginationProps> = ({
    offset,
    total,
    pageSize,
    onPrev,
    onNext,
    className = "",
}) => {
    const { t } = useTranslation();

    if (total <= pageSize && offset === 0) return null;

    return (
        <nav
            aria-label={t("common.pagination", { defaultValue: "Pagination" })}
            className={`flex justify-between items-center mt-4 text-sm text-content-secondary ${className}`}
        >
            <button
                type="button"
                onClick={onPrev}
                disabled={offset === 0}
                className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 disabled:opacity-50"
            >
                {t("common.previous", { defaultValue: "Previous" })}
            </button>

            <span aria-live="polite">
                {t("common.showing", {
                    defaultValue: "Showing {{start}}–{{end}} of {{total}}",
                    start: Math.min(offset + 1, total),
                    end: Math.min(offset + pageSize, total),
                    total,
                })}
            </span>

            <button
                type="button"
                onClick={onNext}
                disabled={offset + pageSize >= total}
                className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 disabled:opacity-50"
            >
                {t("common.next", { defaultValue: "Next" })}
            </button>
        </nav>
    );
};
